import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { ATTENTION_LABEL, ATTENTION_VIEWS, type AttentionView } from '@brickloop/core/schemas';
import { api } from '../api';
import { cn } from '../ui';

/** Where to go to put each view right. */
const FIX: Partial<Record<AttentionView, { to: string; label: string }>> = {
  no_price: { to: '/pricing', label: 'Price these' },
  losing: { to: '/pricing', label: 'Fix prices' },
  thin: { to: '/pricing', label: 'Review prices' },
  no_bin: { to: '/put-away', label: 'Put away' },
  below_reorder: { to: '/buy-list', label: 'Open the buy list' },
};

/** Ready-made views of the lots that need a look, each with how many there are. */
export function AttentionStrip({ active, onPick }: { active?: string; onPick: (v: AttentionView | undefined) => void }) {
  const counts = useQuery({
    queryKey: ['lots-attention'],
    queryFn: () => api<{ counts: Record<AttentionView, number> }>('/api/v1/lots/attention').then((r) => r.counts),
    refetchOnMount: 'always',
  });
  const fix = active ? FIX[active as AttentionView] : undefined;
  return (
    <div className="flex flex-wrap items-center gap-2" aria-label="Views">
      <button
        type="button"
        onClick={() => onPick(undefined)}
        className={cn('rounded-full px-3 py-1 text-sm ring-1', !active ? 'bg-slate-800 text-white ring-slate-800' : 'bg-white text-slate-700 ring-slate-300 hover:bg-slate-50')}
      >
        All
      </button>
      {ATTENTION_VIEWS.map((v) => {
        const n = counts.data?.[v];
        if (n === 0 && active !== v) return null;
        return (
          <button
            key={v}
            type="button"
            onClick={() => onPick(active === v ? undefined : v)}
            className={cn('rounded-full px-3 py-1 text-sm ring-1', active === v ? 'bg-slate-800 text-white ring-slate-800' : 'bg-white text-slate-700 ring-slate-300 hover:bg-slate-50')}
          >
            {ATTENTION_LABEL[v]}
            {n !== undefined && <span className={cn('ml-1.5 tabular-nums', active === v ? 'text-slate-300' : 'text-slate-500')}>{n}</span>}
          </button>
        );
      })}
      {fix && (
        <Link to={fix.to} className="ml-1 text-sm font-medium text-sky-700 hover:underline">
          {fix.label} →
        </Link>
      )}
    </div>
  );
}
